import React, { useState, useRef, useContext, useEffect } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    Select,
    MenuItem,
    FormControl,
    Stack,
    SelectChangeEvent,
    Divider,
    IconButton,
    CircularProgress
} from '@mui/material';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import { useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../../../Helpers/Contexts/authContext';
import { createPhoto } from '../../../../Utils/apiService';
import { enqueueSnackbar } from 'notistack';
import KenLoader from '../../../../GlobalComponents/KenLoader';
import { getCollaborators } from '../../../../Utils/apiService';

const MAX_CAPTION_LENGTH = 50;
const MAX_LOCATION_LENGTH = 250;

interface PhotoAlbumUploadProps {
    onUpload?: (file: File, formData: PhotoFormData) => void;
    albums?: { id: string | number; name: string }[];
    contactId: string;
}

interface PhotoFormData {
    album: string;
    caption: string;
    location: string;
    tags: string[];
}

interface AlbumInfo {
    id: string | number;
    name: string;
}

interface LocationState {
    albums: AlbumInfo[];
    photos: {
        id: string;
        name: string;
        fileLink: string;
        description: string;
        location: string;
        createdDate: string;
    }[];
    selectedAlbum?: string;
}

const PhotoAlbumUpload: React.FC<PhotoAlbumUploadProps> = ({
    onUpload,
    contactId
}) => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState<PhotoFormData>({
        album: '',
        caption: '',
        location: '',
        tags: [],
    });
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [selectedFile, setSelectedFile] = useState<File | null>(null); const fileInputRef = useRef<HTMLInputElement>(null);
    const [isUploading, setIsUploading] = useState<boolean>(false);
    const location = useLocation();
    const { albums = [], selectedAlbum } = (location.state as LocationState) || { albums: [] };
    const { state } = useContext(AuthContext);
    const userData = state.user;
    const [collaborators, setCollaborators] = useState<Array<{ Id: string, Name: string, Profile_Picture_URL__c?: string }>>([]);
    console.log(collaborators, "collaborators")
    const [tagInput, setTagInput] = useState<string>('');
    const [selectedTags, setSelectedTags] = useState<Array<{ Id: string, Name: string }>>([]);
    const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
    const [validationErrors, setValidationErrors] = useState<{
        caption?: string;
        location?: string;
    }>({});

    const MAX_FILE_SIZE = 10 * 1024 * 1024;

    useEffect(() => {
        if (!location.state?.breadCrumbs) {
            navigate(location.pathname, {
                state: {
                    ...location.state,
                    breadCrumbs: 'Photo Upload',
                    previousPath: '/photos'
                },
                replace: true
            });
        }
    }, []);

    useEffect(() => {
        const fetchCollaborators = async () => {
            try {
                const response = await getCollaborators(userData?.applicantId || '');
                console.log(response, "response");
                // Access the nested data structure correctly
                if (response?.data?.data && Array.isArray(response.data.data)) {
                    setCollaborators(response.data.data);
                } else {
                    setCollaborators([]);
                }
            } catch (error) {
                console.error("Error fetching collaborators:", error);
                setCollaborators([]);
            }
        };

        fetchCollaborators();
    }, [userData?.applicantId]);

    useEffect(() => {
        if (selectedAlbum) {
            setFormData(prev => ({
                ...prev,
                album: selectedAlbum
            }));
        }
    }, [selectedAlbum]);

    const handleBackToPhotos = () => {
        navigate('/photos');
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        let error = '';

        // Validate caption and location lengths
        if (name === 'caption' && value.length > MAX_CAPTION_LENGTH) {
            error = `Caption must be ${MAX_CAPTION_LENGTH} characters or less`;
        } else if (name === 'location' && value.length > MAX_LOCATION_LENGTH) {
            error = `Location must be ${MAX_LOCATION_LENGTH} characters or less`;
        }

        setValidationErrors(prev => ({
            ...prev,
            [name]: error
        }));

        if (!error) {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleAlbumChange = (e: SelectChangeEvent) => {
        setFormData({
            ...formData,
            album: e.target.value,
        });
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            const filesArray = Array.from(e.target.files);
            const currentFilenames = new Set(selectedFiles.map(file => file.name));
            const duplicates: string[] = [];
            const largeFiles: string[] = [];
            const newFiles: File[] = [];

            filesArray.forEach(file => {
                if (file.size > MAX_FILE_SIZE) {
                    largeFiles.push(file.name);
                } else if (currentFilenames.has(file.name)) {
                    duplicates.push(file.name);
                } else {
                    newFiles.push(file);
                    currentFilenames.add(file.name);
                }
            });

            if (newFiles.length > 0) {
                setSelectedFiles(prev => [...prev, ...newFiles]);
                if (!selectedFile) {
                    setSelectedFile(newFiles[0]);
                }
            }
            if (duplicates.length > 0) {
                enqueueSnackbar({
                    message: `Duplicate files skipped: ${duplicates.join(', ')}`,
                    variant: 'warning',
                    autoHideDuration: 3000
                });
            }
            if (largeFiles.length > 0) {
                enqueueSnackbar({
                    message: `The following files exceed 10 MB and were not added: ${largeFiles.join(', ')}`,
                    variant: 'warning',
                    autoHideDuration: 3000
                });
            }
            e.target.value = '';
        }
    };


    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            const filesArray = Array.from(e.dataTransfer.files);
            const currentFilenames = new Set(selectedFiles.map(file => file.name));
            const duplicates: string[] = [];
            const largeFiles: string[] = [];
            const newFiles: File[] = [];

            filesArray.forEach(file => {
                if (file.size > MAX_FILE_SIZE) {
                    largeFiles.push(file.name);
                } else if (currentFilenames.has(file.name)) {
                    duplicates.push(file.name);
                } else {
                    newFiles.push(file);
                    currentFilenames.add(file.name);
                }
            });
            if (newFiles.length > 0) {
                setSelectedFiles(prev => [...prev, ...newFiles]);
                if (!selectedFile) {
                    setSelectedFile(newFiles[0]);
                }
            }
            if (duplicates.length > 0) {
                enqueueSnackbar({
                    message: `Duplicate files skipped: ${duplicates.join(', ')}`,
                    variant: 'warning',
                    autoHideDuration: 3000
                });
            }
            if (largeFiles.length > 0) {
                enqueueSnackbar({
                    message: `The following files exceed 10 MB and were not added: ${largeFiles.join(', ')}`,
                    variant: 'warning',
                    autoHideDuration: 3000
                });
            }
        }
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
    };

    const handleTagInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setTagInput(e.target.value);
    };

    const filterCollaborators = () => {
        if (!isDropdownOpen) return [];

        if (!tagInput) {
            return collaborators;
        }
        return collaborators.filter(collab =>
            collab && collab.Name && collab.Name.toLowerCase().includes(tagInput.toLowerCase())
        );
    };


    const handleSelectTag = (collaborator: { Id: string, Name: string }) => {
        if (!selectedTags.some(tag => tag.Id === collaborator.Id)) {
            setSelectedTags([...selectedTags, collaborator]);
            // Update formData to keep track of selected tags (optional)
            setFormData({
                ...formData,
                tags: [...formData.tags, collaborator.Name]
            });
        }
        setTagInput('');
        // setIsDropdownOpen(false); // Remove this line to keep dropdown open
    };

    const handleRemoveTag = (tagId: string) => {
        const updatedTags = selectedTags.filter(tag => tag.Id !== tagId);
        setSelectedTags(updatedTags);
        setFormData({
            ...formData,
            tags: updatedTags.map(tag => tag.Name)
        });
    };

    const handleTagKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && tagInput.trim()) {
            e.preventDefault();
            if (!formData.tags.includes(tagInput.trim())) {
                setFormData({
                    ...formData,
                    tags: [...formData.tags, tagInput.trim()]
                });
            }
            setTagInput('');
        }
    };

    const handleUploadClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        } else {
            console.error("File input reference is not available");
        }
    };


    const handleRemoveFile = () => {
        setSelectedFile(null);
    };

    const checkForDuplicateImage = async (fileName: any) => {
        try {
            const photos = location.state?.photos || [];
            return photos.some((photo: any) => photo.name === fileName);
        } catch (error) {
            console.error("Error checking for duplicate image:", error);
            return false;
        }
    };

    const handlePost = async () => {
        if (selectedFiles.length === 0) {
            enqueueSnackbar({
                message: 'Please select at least one image to upload',
                variant: 'warning',
                autoHideDuration: 3000
            });
            return;
        }

        setIsUploading(true);
        let successCount = 0;
        let failCount = 0;

        try {
            for (const file of selectedFiles) {
                try {
                    const reader = new FileReader();
                    const fileBase64Promise = new Promise<string>((resolve) => {
                        reader.onloadend = () => {
                            const base64String = reader.result as string;
                            const base64Data = base64String.split(',')[1];
                            resolve(base64Data);
                        };
                        reader.readAsDataURL(file);
                    });

                    const base64Data = await fileBase64Promise;
                    const fileData = {
                        contactId: userData?.applicantId || '',
                        fileName: file.name,
                        folder: formData.album,
                        caption: formData.caption,
                        location: formData.location,
                        taggedAlumniIds: selectedTags.map(tag => tag.Id),
                        image: {
                            fileName: file.name,
                            fileBlob: base64Data
                        }
                    };

                    console.log("Contact ID being sent:", contactId);

                    const response = await createPhoto(fileData as any);
                    console.log("Photo upload response:", response);

                    if (response && response.status === 200 || response.success === true || (!response.error && response.data)) {
                        successCount++;
                        if (onUpload) {
                            onUpload(file, formData);
                        }
                    } else {
                        failCount++;
                    }
                } catch (error) {
                    console.error("Error uploading photo:", error);
                    failCount++;
                }
            }
            if (successCount > 0) {
                enqueueSnackbar({
                    message: `Successfully uploaded ${successCount} image${successCount > 1 ? 's' : ''}${failCount > 0 ? `, ${failCount} failed` : ''}`,
                    variant: 'success',
                    autoHideDuration: 3000
                });

                navigate('/photos');
                localStorage.setItem('photoAlbumActiveTab', '1');
            } else {
                enqueueSnackbar({
                    message: 'Failed to upload any images. Please try again.',
                    variant: 'error',
                    autoHideDuration: 3000
                });
            }

            if (successCount > 0) {
                handleCancel();
            }
        } catch (error) {
            console.error("Error in batch upload:", error);
            enqueueSnackbar({
                message: 'Something went wrong. Please try again.',
                variant: 'error',
                autoHideDuration: 3000
            });
        } finally {
            setIsUploading(false);
        }
    };
    const handleCancel = () => {
        setSelectedFile(null);
        setSelectedFiles([]);
        setFormData({
            album: '',
            caption: '',
            location: '',
            tags: [],
        });
        setTagInput('');
        setIsUploading(false);
        navigate('/photos');
    };

    return (
        <Box sx={{
            display: 'flex',
            width: '100%',
            gap: 3,
            height: '83.5vh',
            flexDirection: { xs: 'column', md: 'row' },
            '@media (min-width: 1280px) and (max-width: 1300px)': {
                height: '81vh',
            },
            '@media (max-width: 1600px)': {
                height: '83.5vh',
            },
            '@media (max-width: 1360px)': {
                height: '100vh',
            },
            '@media (max-width: 900px)': {
                height: 'auto',
                minHeight: '100vh',
            }
        }}>
            <Box
                sx={{
                    flex: 1,
                    backgroundColor: '#fff',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: { xs: 'auto', md: '100%' },
                    minHeight: { xs: '350px', md: '100%' }
                }}
            >
                <Box
                    sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: { xs: 2, md: 3 },
                        minHeight: 400,
                        position: 'relative'
                    }}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                >
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileSelect}
                        accept="image/jpeg,image/png,image/jpg"
                        multiple
                        style={{ display: 'none' }}
                    />
                    {selectedFiles && selectedFiles.length > 0 ? (
                        <Box sx={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            p: { xs: 1, md: 1 }
                        }}>
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                width: '100%',
                                mb: 2,
                                flexWrap: 'nowrap',
                                gap: 1
                            }}>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontSize: '18px',
                                        fontWeight: 500,
                                        color: '#344054',
                                        flexShrink: 1,
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}
                                >
                                    Uploaded images
                                </Typography>

                                <Button
                                    variant="contained"
                                    color='primary'
                                    size='medium'
                                    startIcon={<FileUploadIcon />}
                                    onClick={handleUploadClick}
                                    sx={{ whiteSpace: 'nowrap' }}
                                >
                                    Add more photos
                                </Button>
                            </Box>

                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2,
                                height: { xs: 'auto', md: 'calc(100% - 60px)' },
                                maxHeight: { xs: '400px', md: 'none' },
                                overflowY: 'auto',
                                width: '100%',
                                pb: 2
                            }}>
                                {selectedFiles.map((file, index) => (
                                    <Box
                                        key={index}
                                        sx={{
                                            position: 'relative',
                                            width: '100%',
                                            height: { xs: '180px', sm: '220px' },
                                            borderRadius: '8px',
                                            overflow: 'hidden',
                                            flexShrink: 0
                                        }}
                                    >
                                        <Box
                                            component="img"
                                            src={URL.createObjectURL(file)}
                                            alt={`Preview ${index}`}
                                            sx={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover',
                                                borderRadius: '8px'
                                            }}
                                        />
                                        <IconButton
                                            sx={{
                                                position: 'absolute',
                                                top: 8,
                                                right: 8,
                                                backgroundColor: 'rgba(255,255,255,0.8)',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(255,255,255,0.9)'
                                                },
                                                padding: { xs: '6px', md: '8px' }
                                            }}
                                            onClick={() => {
                                                const newFiles = selectedFiles.filter((_, i) => i !== index);
                                                setSelectedFiles(newFiles);
                                                if (newFiles.length > 0) {
                                                    setSelectedFile(newFiles[0]);
                                                } else {
                                                    setSelectedFile(null);
                                                }
                                            }}
                                        >
                                            <DeleteOutlineIcon />
                                        </IconButton>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                border: '1px dashed #B2B3B7',
                                borderRadius: '8px',
                                width: { xs: '90%', sm: '400px' },
                                height: { xs: '300px', sm: '400px' },
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                p: { xs: 1.5, sm: 3 },
                                margin: 'auto'
                            }}
                        >
                            <Box sx={{ mb: 2, color: '#667085' }}>
                                <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M26.6666 21.6667V26.6667H13.3333V21.6667H10V26.6667C10 28.5 11.5 30 13.3333 30H26.6666C28.5 30 30 28.5 30 26.6667V21.6667H26.6666ZM15 15L17.3333 17.3333L18.3333 16.3333V23.3333H21.6666V16.3333L23.6666 18.3333L26 16L20 10L15 15Z" fill="#667085" />
                                </svg>
                            </Box>
                            <Typography variant="h6" sx={{
                                mb: 2,
                                fontWeight: 500,
                                color: '#344054',
                                fontSize: { xs: '16px', sm: '18px' }
                            }}>
                                Drag and drop your images here
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 1, color: '#667085', fontSize: '14px', fontWeight: 400 }}>
                                Max file size 10 mb
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 3, color: '#667085', fontSize: '14px', fontWeight: 400 }}>
                                Supported formats: JPEG, PNG, JPG
                            </Typography>
                            <Button
                                variant="contained"
                                color='secondary'
                                startIcon={<FileUploadIcon />}
                                onClick={handleUploadClick}
                            // sx={{
                            //     background: '#373A45',
                            //     '&:hover': {
                            //         background: '#373A45'
                            //     },
                            //     borderRadius: '8px',
                            //     textTransform: 'none',
                            //     px: 3,
                            //     py: 1
                            // }}
                            >
                                Upload image
                            </Button>
                        </Box>
                    )}
                </Box>
            </Box>

            <Box
                sx={{
                    width: { xs: '100%', md: '400px' },
                    backgroundColor: '#fff',
                    borderRadius: '12px',
                    display: 'flex',
                    flexDirection: 'column',
                    marginTop: { xs: 2, md: 0 }
                }}
            >
                <Box sx={{ p: { xs: 2, md: 3 } }}>
                    <Typography
                        variant="h6"
                        sx={{
                            fontSize: '18px',
                            fontWeight: 500,
                            color: '#344054',
                            textAlign: { xs: 'left', sm: 'left' },
                        }}
                    >
                        Image details
                    </Typography>
                </Box>

                <Divider sx={{ borderColor: '#D9DBE1' }} />

                <Box sx={{ p: { xs: 2, md: 3 }, flex: 1 }}>
                    <Stack spacing={3}>
                        <Box sx={{ textAlign: "start" }}>
                            <Typography component="span" variant="body2" sx={{ mb: 1, fontWeight: 500, color: '#344054', fontSize: '14px' }}>
                                Add to album
                            </Typography>
                            <Typography component="span" variant="body2" sx={{ mb: 1, fontWeight: 400, color: '#667085', fontSize: '14px' }}>
                                (optional)
                            </Typography>
                            <FormControl fullWidth sx={{ marginTop: "5px" }}>
                                <Select
                                    value={formData.album}
                                    onChange={handleAlbumChange}
                                    displayEmpty
                                    name="album"
                                    renderValue={formData.album ? undefined : () => "Select"}
                                    sx={{
                                        height: '48px',
                                        '.MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#B2B3B7',
                                            borderWidth: '0.5px'
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#A3A9B5'
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#6C90BD'
                                        }
                                    }}
                                >
                                    <MenuItem value="" sx={{ color: '#667085' }}>
                                        None
                                    </MenuItem>
                                    {albums.map((album) => (
                                        <MenuItem key={album.id} value={album.id}>
                                            {album.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                        <Box sx={{ textAlign: "start" }}>
                            <Typography component="span" variant="body2" sx={{ mb: 1, fontWeight: 500, color: '#344054', fontSize: '14px' }}>
                                Caption
                            </Typography>
                            <Typography component="span" variant="body2" sx={{ mb: 1, fontWeight: 400, color: '#667085', fontSize: '14px' }}>
                                (optional, max {MAX_CAPTION_LENGTH} characters)
                            </Typography>
                            <TextField
                                fullWidth
                                name="caption"
                                placeholder="Enter caption"
                                value={formData.caption}
                                onChange={handleChange}
                                error={!!validationErrors.caption}
                                helperText={validationErrors.caption}
                                variant="outlined"
                                sx={{
                                    '.MuiOutlinedInput-root': {
                                        height: '48px',
                                    },
                                    '.MuiOutlinedInput-notchedOutline': {
                                        borderColor: validationErrors.caption ? '#D32F2F' : '#B2B3B7',
                                        borderWidth: '0.5px'
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: validationErrors.caption ? '#D32F2F' : '#A3A9B5'
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: validationErrors.caption ? '#D32F2F' : '#6C90BD'
                                    },
                                    marginTop: "5px"
                                }}
                            />
                        </Box>

                        <Box sx={{ textAlign: "start" }}>
                            <Typography component="span" variant="body2" sx={{ mb: 1, fontWeight: 500, color: '#344054', fontSize: '14px' }}>
                                Add location
                            </Typography>
                            <Typography component="span" variant="body2" sx={{ mb: 1, fontWeight: 400, color: '#667085', fontSize: '14px' }}>
                                (optional, max {MAX_LOCATION_LENGTH} characters)
                            </Typography>
                            <TextField
                                fullWidth
                                name="location"
                                placeholder="Enter location"
                                value={formData.location}
                                onChange={handleChange}
                                error={!!validationErrors.location}
                                helperText={validationErrors.location}
                                variant="outlined"
                                sx={{
                                    '.MuiOutlinedInput-root': {
                                        height: '48px'
                                    },
                                    '.MuiOutlinedInput-notchedOutline': {
                                        borderColor: validationErrors.location ? '#D32F2F' : '#B2B3B7',
                                        borderWidth: '0.5px'
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: validationErrors.location ? '#D32F2F' : '#A3A9B5'
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: validationErrors.location ? '#D32F2F' : '#6C90BD'
                                    },
                                    marginTop: "5px"
                                }}
                            />
                        </Box>

                        <Box sx={{ textAlign: "start" }}>
                            <Typography component="span" variant="body2" sx={{ mb: 1, fontWeight: 500, color: '#344054', fontSize: '14px' }}>
                                Tag other alumni
                            </Typography>
                            <Typography component="span" variant="body2" sx={{ mb: 1, fontWeight: 400, color: '#667085', fontSize: '14px' }}>
                                (optional)
                            </Typography>
                            <Box sx={{ position: 'relative', width: '100%' }}>
                                <TextField
                                    fullWidth
                                    placeholder="Type to search alumni"
                                    value={tagInput}
                                    onChange={handleTagInputChange}
                                    onFocus={() => setIsDropdownOpen(true)}
                                    onBlur={() => setIsDropdownOpen(false)}
                                    variant="outlined"
                                    sx={{
                                        '.MuiOutlinedInput-root': {
                                            height: '48px'
                                        },
                                        '.MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#B2B3B7',
                                            borderWidth: '0.5px'
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#A3A9B5'
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#6C90BD'
                                        },
                                        marginTop: "5px"
                                    }}
                                />
                                {isDropdownOpen && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: '100%',
                                            left: 0,
                                            right: 0,
                                            zIndex: 1000,
                                            backgroundColor: 'white',
                                            borderRadius: '8px',
                                            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                                            maxHeight: '300px',
                                            overflowY: 'auto',
                                            mt: 0.5,
                                            border: '1px solid #E6E6E8'
                                        }}
                                    >
                                        {filterCollaborators().map((collab) => (
                                            <Box
                                                key={collab.Id}
                                                onMouseDown={(e) => {
                                                    e.preventDefault();
                                                    handleSelectTag(collab);
                                                    // setIsDropdownOpen(false); // Remove this line to keep dropdown open
                                                }}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    p: 1.5,
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        backgroundColor: '#F8F9FA'
                                                    },
                                                    borderBottom: '1px solid #E6E6E8'
                                                }}
                                            >
                                                {collab.Profile_Picture_URL__c ? (
                                                    <Box
                                                        component="img"
                                                        src={collab.Profile_Picture_URL__c}
                                                        sx={{
                                                            width: 32,
                                                            height: 32,
                                                            borderRadius: '50%',
                                                            objectFit: 'cover'
                                                        }}
                                                    />
                                                ) : (
                                                    <Box sx={{
                                                        width: 32,
                                                        height: 32,
                                                        borderRadius: '50%',
                                                        bgcolor: '#E0E4EB',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontSize: '14px',
                                                        fontWeight: 500,
                                                        color: '#667085'
                                                    }}>
                                                        {collab.Name.charAt(0)}
                                                    </Box>
                                                )}
                                                <Typography sx={{ fontSize: '14px', color: '#344054' }}>
                                                    {collab.Name}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                                {selectedTags.length > 0 && (
                                    <Box
                                        sx={{
                                            mt: 1,
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            gap: 1,
                                            alignItems: 'flex-start',
                                            maxHeight: { xs: '160px', sm: isDropdownOpen ? '100px' : 'none' },
                                            overflowY: { xs: 'auto', sm: isDropdownOpen ? 'auto' : 'visible' },
                                            p: 1,
                                            border: '1px solid #E6E6E8',
                                            borderRadius: '8px',
                                            backgroundColor: '#F8F9FA'
                                        }}
                                    >
                                        {selectedTags.map((tag) => (
                                            <Box
                                                key={tag.Id}
                                                sx={{
                                                    bgcolor: 'white',
                                                    borderRadius: '4px',
                                                    py: 0.5,
                                                    px: 1.5,
                                                    fontSize: '14px',
                                                    color: '#344054',
                                                    border: '1px solid #D9DBE1',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 0.5
                                                }}
                                            >
                                                {tag.Name}
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleRemoveTag(tag.Id)}
                                                    sx={{
                                                        p: 0.5,
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                                        }
                                                    }}
                                                >
                                                    <CloseIcon sx={{ fontSize: 16, color: '#667085' }} />
                                                </IconButton>
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </Stack>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: { xs: 2, md: 3 }, gap: 2, flexDirection: "row" }}>
                    <Button
                        variant="contained"
                        color='secondary'
                        onClick={handleCancel}

                        disabled={isUploading}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color='primary'
                        onClick={handlePost}
                        disabled={selectedFiles.length === 0 || isUploading}

                    >
                        {isUploading ? 'Posting...' : 'Post'}
                    </Button>
                </Box>
            </Box>
            {isUploading && <KenLoader />}
        </Box>
    );
};

export default PhotoAlbumUpload;
